import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    MenuItem,
    Grid,
    Tabs,
    Tab,
    TableRow,
    TableCell,
    tableCellClasses,
    Chip,
    Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import {
    getMonthlyExpenseReport,
    getCategoryExpenseReport,
    getCustomExpenseReport,
    getDailyExpenseExport,
    getExpenseCategories,
} from '../../Redux/Actions/action';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import CommonTable from '../../Components/Common/CommonTable';
import DataNotFound from '../../Components/Common/DataNotFound';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CategoryIcon from '@mui/icons-material/Category';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TodayIcon from '@mui/icons-material/Today';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        textAlign: 'center',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
        padding: '10px 8px',
    },
}));

const ExpenseReports = () => {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(0);
    const [categories, setCategories] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState({
        month: dayjs().month() + 1,
        year: dayjs().year(),
        fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        toDate: dayjs().format('YYYY-MM-DD'),
        category: '',
        status: '',
    });

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - i);

    useEffect(() => {
        const fetchCategories = async () => {
            const result = await dispatch(getExpenseCategories({}));
            if (result?.payload?.success) {
                setCategories(result?.payload?.data || []);
            }
        };
        fetchCategories();
    }, [dispatch]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const generateReport = async () => {
        dispatch(showLoader(true));
        try {
            let result;
            switch (activeTab) {
                case 0: // Monthly Report
                    result = await dispatch(getMonthlyExpenseReport({
                        month: filters.month,
                        year: filters.year,
                    }));
                    break;
                case 1: // Category Report
                    result = await dispatch(getCategoryExpenseReport({
                        fromDate: filters.fromDate,
                        toDate: filters.toDate,
                        categoryId: filters.category || undefined,
                    }));
                    break;
                case 2: // Custom Report
                    result = await dispatch(getCustomExpenseReport({
                        fromDate: filters.fromDate,
                        toDate: filters.toDate,
                        status: filters.status || undefined,
                        category: filters.category || undefined,
                    }));
                    break;
                case 3: // Daily Export
                    result = await dispatch(getDailyExpenseExport({
                        date: filters.fromDate,
                    }));
                    break;
                default:
                    break;
            }

            if (ErrorHandler(result)) {
                setReportData(result?.payload?.data);
                toast.success('Report generated successfully');
            }
        } catch (error) {
            toast.error('Failed to generate report');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const exportToExcel = async () => {
        if (!reportData) {
            toast.warning('Please generate a report first');
            return;
        }

        dispatch(showLoader(true));
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Expense Report');

            // Title
            const reportTitles = ['Monthly Report', 'Category Report', 'Custom Report', 'Daily Export'];
            worksheet.mergeCells('A1:I1');
            worksheet.getCell('A1').value = `Expense ${reportTitles[activeTab]}`;
            worksheet.getCell('A1').font = { bold: true, size: 16 };
            worksheet.getCell('A1').alignment = { horizontal: 'center' };

            // Date info
            worksheet.mergeCells('A2:I2');
            worksheet.getCell('A2').value = `Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`;
            worksheet.getCell('A2').alignment = { horizontal: 'center' };

            worksheet.addRow([]);

            if (activeTab === 0 || activeTab === 2 || activeTab === 3) {
                // Expense details export
                const expenses = reportData?.expenses || [];
                
                // Summary
                const summary = reportData?.summary || {};
                worksheet.addRow(['Summary']);
                worksheet.addRow(['Total Amount', `₹${summary.totalAmount?.toLocaleString() || 0}`]);
                worksheet.addRow(['Approved', `₹${summary.approvedAmount?.toLocaleString() || 0}`]);
                worksheet.addRow(['Pending', `₹${summary.pendingAmount?.toLocaleString() || 0}`]);
                worksheet.addRow(['Rejected', `₹${summary.rejectedAmount?.toLocaleString() || 0}`]);
                worksheet.addRow([]);

                // Headers
                const headers = ['Expense ID', 'Date', 'Title', 'Category', 'Amount', 'Paid To', 'Payment Mode', 'Status', 'Created By'];
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                headerRow.eachCell((cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3E4094' } };
                    cell.alignment = { horizontal: 'center' };
                });

                // Data rows
                expenses.forEach((exp) => {
                    worksheet.addRow([
                        exp.expenseId || exp['Expense ID'] || '-',
                        exp.expenseDate ? dayjs(exp.expenseDate).format('DD/MM/YYYY') : (exp.Date || '-'),
                        exp.title || exp.Title || '-',
                        exp.category?.name || exp.Category || '-',
                        exp.amount || exp.Amount || 0,
                        exp.paidTo || exp['Paid To'] || '-',
                        exp.paymentMode || exp['Payment Mode'] || '-',
                        exp.status || exp.Status || '-',
                        exp.createdByName || exp['Created By'] || '-',
                    ]);
                });

                // Auto-fit columns
                worksheet.columns.forEach((column) => {
                    column.width = 18;
                });
            } else if (activeTab === 1) {
                // Category report
                const headers = ['Category', 'Count', 'Total Amount', 'Approved', 'Pending', 'Budget Utilization'];
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
                headerRow.eachCell((cell) => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3E4094' } };
                    cell.alignment = { horizontal: 'center' };
                });

                (reportData || []).forEach((cat) => {
                    worksheet.addRow([
                        cat.categoryName || '-',
                        cat.count || 0,
                        cat.totalAmount || 0,
                        cat.approvedAmount || 0,
                        cat.pendingAmount || 0,
                        cat.budgetUtilization ? `${cat.budgetUtilization.toFixed(1)}%` : '-',
                    ]);
                });

                worksheet.columns.forEach((column) => {
                    column.width = 20;
                });
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Expense_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
            toast.success('Exported successfully');
        } catch (error) {
            toast.error('Export failed');
            console.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const renderFilters = () => {
        switch (activeTab) {
            case 0: // Monthly
                return (
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                        <TextField
                            select
                            label="Month"
                            value={filters.month}
                            onChange={(e) => handleFilterChange('month', e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            {months.map((m) => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Year"
                            value={filters.year}
                            onChange={(e) => handleFilterChange('year', e.target.value)}
                            size="small"
                            sx={{ minWidth: 100 }}
                        >
                            {years.map((y) => (
                                <MenuItem key={y} value={y}>{y}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                );
            case 1: // Category
            case 2: // Custom
                return (
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                        <TextField
                            type="date"
                            label="From Date"
                            value={filters.fromDate}
                            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            label="To Date"
                            value={filters.toDate}
                            onChange={(e) => handleFilterChange('toDate', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            select
                            label="Category"
                            value={filters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                            ))}
                        </TextField>
                        {activeTab === 2 && (
                            <TextField
                                select
                                label="Status"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                size="small"
                                sx={{ minWidth: 120 }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Rejected">Rejected</MenuItem>
                            </TextField>
                        )}
                    </Box>
                );
            case 3: // Daily
                return (
                    <Box display="flex" gap={2} alignItems="center">
                        <TextField
                            type="date"
                            label="Date"
                            value={filters.fromDate}
                            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                );
            default:
                return null;
        }
    };

    const renderReportContent = () => {
        if (!reportData) {
            return (
                <Box textAlign="center" py={8}>
                    <Typography color="textSecondary">Select filters and click "Generate Report"</Typography>
                </Box>
            );
        }

        if (activeTab === 1) {
            // Category report
            const data = reportData || [];
            return (
                <CommonTable headerList={['Category', 'Expenses', 'Total Amount', 'Approved', 'Pending', 'Budget %']} minWidth={700}>
                    {data.length > 0 ? (
                        data.map((cat, idx) => (
                            <TableRow key={idx}>
                                <StyledTableCell sx={{ fontWeight: 600 }}>
                                    {cat.categoryIcon} {cat.categoryName}
                                </StyledTableCell>
                                <StyledTableCell>{cat.count}</StyledTableCell>
                                <StyledTableCell sx={{ fontWeight: 600 }}>₹{cat.totalAmount?.toLocaleString()}</StyledTableCell>
                                <StyledTableCell sx={{ color: 'green' }}>₹{cat.approvedAmount?.toLocaleString()}</StyledTableCell>
                                <StyledTableCell sx={{ color: 'orange' }}>₹{cat.pendingAmount?.toLocaleString()}</StyledTableCell>
                                <StyledTableCell>
                                    {cat.budgetUtilization ? (
                                        <Chip
                                            label={`${cat.budgetUtilization.toFixed(1)}%`}
                                            color={cat.budgetUtilization > 100 ? 'error' : cat.budgetUtilization > 80 ? 'warning' : 'success'}
                                            size="small"
                                        />
                                    ) : '-'}
                                </StyledTableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <StyledTableCell colSpan={6}>
                                <DataNotFound title="No Data" icon={<ErrorOutlineIcon />} elevation={0} />
                            </StyledTableCell>
                        </TableRow>
                    )}
                </CommonTable>
            );
        }

        // Expense list reports
        const expenses = reportData?.expenses || [];
        const summary = reportData?.summary || {};

        return (
            <Box>
                {/* Summary */}
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                            <Typography variant="body2" color="textSecondary">Total</Typography>
                            <Typography variant="h6" fontWeight={600}>₹{summary.totalAmount?.toLocaleString() || 0}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 2 }}>
                            <Typography variant="body2" color="textSecondary">Approved</Typography>
                            <Typography variant="h6" fontWeight={600} color="success.main">
                                ₹{summary.approvedAmount?.toLocaleString() || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0', borderRadius: 2 }}>
                            <Typography variant="body2" color="textSecondary">Pending</Typography>
                            <Typography variant="h6" fontWeight={600} color="warning.main">
                                ₹{summary.pendingAmount?.toLocaleString() || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee', borderRadius: 2 }}>
                            <Typography variant="body2" color="textSecondary">Rejected</Typography>
                            <Typography variant="h6" fontWeight={600} color="error.main">
                                ₹{summary.rejectedAmount?.toLocaleString() || 0}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Table */}
                <CommonTable headerList={['No', 'Expense ID', 'Date', 'Title', 'Category', 'Amount', 'Status']} minWidth={800}>
                    {expenses.length > 0 ? (
                        expenses.map((exp, idx) => (
                            <TableRow key={idx}>
                                <StyledTableCell>{idx + 1}</StyledTableCell>
                                <StyledTableCell sx={{ fontWeight: 600, color: '#3E4094' }}>
                                    {exp.expenseId || exp['Expense ID']}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {exp.expenseDate ? dayjs(exp.expenseDate).format('DD/MM/YYYY') : (exp.Date || '-')}
                                </StyledTableCell>
                                <StyledTableCell>{exp.title || exp.Title}</StyledTableCell>
                                <StyledTableCell>{exp.category?.name || exp.Category || '-'}</StyledTableCell>
                                <StyledTableCell sx={{ fontWeight: 600 }}>
                                    ₹{(exp.amount || exp.Amount)?.toLocaleString()}
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Chip
                                        label={exp.status || exp.Status}
                                        size="small"
                                        color={
                                            (exp.status || exp.Status) === 'Approved' ? 'success' :
                                            (exp.status || exp.Status) === 'Pending' ? 'warning' :
                                            (exp.status || exp.Status) === 'Rejected' ? 'error' : 'default'
                                        }
                                    />
                                </StyledTableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <StyledTableCell colSpan={7}>
                                <DataNotFound title="No Expenses Found" icon={<ErrorOutlineIcon />} elevation={0} />
                            </StyledTableCell>
                        </TableRow>
                    )}
                </CommonTable>
            </Box>
        );
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        Expense Reports
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToExcel}
                        disabled={!reportData}
                    >
                        Export Excel
                    </Button>
                </Box>

                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => { setActiveTab(v); setReportData(null); }}
                    sx={{ mb: 3 }}
                >
                    <Tab icon={<CalendarMonthIcon />} label="Monthly" iconPosition="start" />
                    <Tab icon={<CategoryIcon />} label="Category-wise" iconPosition="start" />
                    <Tab icon={<DateRangeIcon />} label="Custom Range" iconPosition="start" />
                    <Tab icon={<TodayIcon />} label="Daily Export" iconPosition="start" />
                </Tabs>

                <Divider sx={{ mb: 3 }} />

                {/* Filters */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                    {renderFilters()}
                    <Button variant="contained" onClick={generateReport}>
                        Generate Report
                    </Button>
                </Box>

                {/* Report Content */}
                {renderReportContent()}
            </Paper>
        </Box>
    );
};

export default ExpenseReports;
